import * as React from 'react';
import { useEffect, useState, useCallback } from 'react';
import { SyncIcon } from '@patternfly/react-icons';
import toast from 'react-hot-toast';
import { AdminAd, AD_STATES, AD_TYPES, adStateTone } from '@app/Moderation/types';
import { getAds, moderateAd } from '@app/Moderation/moderation.api';
import { Pill } from '@app/Campaigns/badges';
import { relativeTime, formatDateTime } from '@app/Screens/format';
import { colors, radii, shadows } from '@app/components/designTokens';

export const Moderation: React.FunctionComponent = () => {
  const [ads, setAds] = useState<AdminAd[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stateFilter, setStateFilter] = useState('PENDING_REVIEW');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(
    async (spinner: boolean) => {
      if (spinner) setLoading(true);
      try {
        const data = await getAds({
          state: stateFilter || undefined,
          type: typeFilter || undefined,
          q: search || undefined,
          limit: 200
        });
        setAds(data);
        setError(null);
      } catch (e: any) {
        setError(e?.message || 'Failed to load ads');
        if (!spinner) toast.error('Could not refresh ads');
      } finally {
        if (spinner) setLoading(false);
      }
    },
    [stateFilter, typeFilter, search]
  );

  useEffect(() => {
    load(true);
  }, [load]);

  const onModerate = async (ad: AdminAd, decision: 'APPROVE' | 'REJECT') => {
    if (decision === 'REJECT' && !window.confirm(`Reject "${ad.name || `ad #${ad.adId}`}"? It will be marked MODERATION_FAILED.`)) {
      return;
    }
    setBusyId(ad.adId);
    try {
      const res = await moderateAd(ad.adId, decision);
      toast(`Ad ${decision === 'APPROVE' ? 'approved' : 'rejected'} → ${res.adState}`);
      // Update in place so the operator sees the new state; drop it if it no longer matches the filter.
      setAds(prev =>
        prev
          .map(a => (a.adId === ad.adId ? { ...a, adState: res.adState } : a))
          .filter(a => !stateFilter || a.adState === stateFilter)
      );
    } catch (e: any) {
      toast.error(e?.message || 'Moderation failed');
    } finally {
      setBusyId(null);
    }
  };

  const styles: { [k: string]: React.CSSProperties } = {
    wrap: { padding: 24 },
    headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 },
    title: { fontSize: 22, fontWeight: 700, color: colors.title, margin: 0 },
    subtle: { fontSize: 13, color: colors.subtle },
    refreshBtn: { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px', border: `1px solid ${colors.border}`, borderRadius: radii.button, background: '#fff', color: colors.body, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
    toolbar: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 },
    search: { flex: 1, minWidth: 200, maxWidth: 320, padding: '10px 12px', border: `1px solid ${colors.border}`, borderRadius: radii.input, fontSize: 14, outline: 'none' },
    select: { padding: '10px 12px', border: `1px solid ${colors.border}`, borderRadius: radii.input, fontSize: 14, background: '#fff', color: colors.body },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 },
    card: { background: '#fff', border: `1px solid ${colors.border}`, borderRadius: radii.card, boxShadow: shadows.softCard, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
    thumb: { width: '100%', height: 160, objectFit: 'cover', background: colors.bgMuted, display: 'block' },
    thumbPlaceholder: { width: '100%', height: 160, background: colors.bgMuted, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.placeholder, fontSize: 13 },
    body: { padding: 16, display: 'flex', flexDirection: 'column', gap: 8, flex: 1 },
    name: { fontWeight: 700, color: colors.title, fontSize: 15 },
    meta: { fontSize: 12, color: colors.subtle },
    prompt: { fontSize: 13, color: colors.muted, lineHeight: 1.5, maxHeight: 76, overflow: 'hidden', background: colors.bgMuted, borderRadius: 8, padding: '8px 10px', fontStyle: 'italic' },
    actions: { display: 'flex', gap: 8, marginTop: 'auto', paddingTop: 8 },
    approve: { flex: 1, padding: '9px 12px', border: 'none', borderRadius: radii.button, background: '#059669', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
    reject: { flex: 1, padding: '9px 12px', border: `1px solid ${colors.danger}`, borderRadius: radii.button, background: '#fff', color: colors.danger, fontSize: 13, fontWeight: 700, cursor: 'pointer' }
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.headerRow}>
        <div>
          <h1 style={styles.title}>Moderation</h1>
          <div style={styles.subtle}>
            {loading ? 'Loading…' : `${ads.length} ad${ads.length === 1 ? '' : 's'}${stateFilter ? ` in ${stateFilter.replace(/_/g, ' ')}` : ''}`}
          </div>
        </div>
        <button style={styles.refreshBtn} onClick={() => load(true)}>
          <SyncIcon className={loading ? 'logo-bolt-pulse' : undefined} /> Refresh
        </button>
      </div>

      <div style={styles.toolbar}>
        <input style={styles.search} placeholder="Search name, prompt, creator…" value={search} onChange={e => setSearch(e.target.value)} />
        <select style={styles.select} value={stateFilter} onChange={e => setStateFilter(e.target.value)}>
          <option value="">All states</option>
          {AD_STATES.map(s => (
            <option key={s} value={s}>
              {s.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
        <select style={styles.select} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All types</option>
          {AD_TYPES.map(t => (
            <option key={t} value={t}>
              {t === 'GENERATE_WITH_AI' ? 'AI-generated' : 'Uploaded'}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div style={{ background: colors.dangerRing, color: colors.danger, borderRadius: 10, padding: '12px 16px', fontSize: 14, marginBottom: 12 }}>{error}</div>
      )}

      {loading ? (
        <div style={{ ...styles.card, padding: 48, textAlign: 'center', color: colors.subtle }}>Loading ads…</div>
      ) : ads.length === 0 ? (
        <div style={{ background: '#fff', border: `1px solid ${colors.border}`, borderRadius: radii.card, padding: 48, textAlign: 'center', color: stateFilter === 'PENDING_REVIEW' ? '#047857' : colors.subtle, fontWeight: stateFilter === 'PENDING_REVIEW' ? 600 : 400 }}>
          {stateFilter === 'PENDING_REVIEW' ? 'Nothing awaiting review. 🎉' : 'No ads match these filters.'}
        </div>
      ) : (
        <div style={styles.grid}>
          {ads.map(ad => (
            <div key={ad.adId} style={styles.card}>
              {ad.thumbnailUrl ? (
                <img src={ad.thumbnailUrl} alt={ad.name || `ad ${ad.adId}`} style={styles.thumb} />
              ) : (
                <div style={styles.thumbPlaceholder}>no preview</div>
              )}
              <div style={styles.body}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={styles.name}>{ad.name || `Ad #${ad.adId}`}</span>
                  <Pill text={ad.adState || 'UNKNOWN'} tone={adStateTone(ad.adState)} />
                </div>
                <div style={styles.meta}>
                  {ad.adType === 'GENERATE_WITH_AI' ? 'AI-generated' : 'Uploaded'} · {ad.creatorEmail || '—'}
                  {ad.orgName ? ` · ${ad.orgName}` : ''}
                </div>
                {ad.prompt && <div style={styles.prompt}>“{ad.prompt}”</div>}
                <div style={styles.meta} title={formatDateTime(ad.createDateTime)}>Created {relativeTime(ad.createDateTime)}</div>
                <div style={styles.actions}>
                  <button style={styles.approve} disabled={busyId === ad.adId} onClick={() => onModerate(ad, 'APPROVE')}>
                    Approve
                  </button>
                  <button style={styles.reject} disabled={busyId === ad.adId} onClick={() => onModerate(ad, 'REJECT')}>
                    Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
